/**
 * Konfiguracja widoku listy — kontrakt na to co siedzi w kolumnie `list_config`
 * (JSONB w bazie) dla layoutów modułów systemowych i custom.
 *
 * Port z aplikacji SellCall (apps/admin/src/components/configurable-table/types.ts).
 * Trzymane jako Zod schema żeby:
 *   - backend mógł walidować body endpointów zapisu widoku,
 *   - frontend i backend miały to samo źródło prawdy o shape'ie,
 *   - typy TS pochodziły z .infer<> (zero duplikacji).
 *
 * Konsumenci:
 *   - apps/api: PUT/POST /api/system-modules/:slug/layouts/...
 *   - apps/api: PUT/POST /api/modules/:id/layouts/...
 *   - apps/admin: komponenty listy + modal "Ustawienia widoku".
 */

import { z } from 'zod';

import { listActionConfigSchema } from './list-actions';
import { widgetsConfigSchema } from './list-view-widgets';

// ── Kolumna ──────────────────────────────────────────────────────────────────

/**
 * Ustawienia pojedynczej kolumny w widoku — zapamiętane preferencje usera
 * (które przesłaniają defaulty z definicji kolumny po stronie frontu).
 */
export const columnConfigSchema = z.object({
  /** Identyfikator kolumny — odpowiada `ColumnDefinition.id` po stronie frontu. */
  id: z.string().min(1),
  /** Czy kolumna widoczna. */
  visible: z.boolean(),
  /** Szerokość: liczba (px) lub string (np. "20%"). */
  width: z.union([z.number().int().positive(), z.string().min(1)]),
  /** Etykieta nadpisana przez usera (undefined = użyj domyślnej). */
  customLabel: z.string().optional(),
  /** Kolejność wyświetlania (0-indexed). */
  order: z.number().int().nonnegative(),
});

export type ColumnConfig = z.infer<typeof columnConfigSchema>;

// ── Sortowanie ───────────────────────────────────────────────────────────────

/**
 * Domyślne sortowanie listy przy pierwszym wejściu.
 */
export const sortConfigSchema = z.object({
  /** Identyfikator kolumny po której sortujemy. */
  column: z.string().min(1),
  /** Kierunek: rosnąco / malejąco. */
  direction: z.enum(['asc', 'desc']),
});

export type SortConfig = z.infer<typeof sortConfigSchema>;

// ── Filtry ───────────────────────────────────────────────────────────────────

/**
 * Operatory porównania dla domyślnych filtrów listy.
 * Zestaw kopiowany 1:1 z SellCall — pokrywa wszystkie typowe przypadki
 * (równe, zawiera, większe, między, jest puste itp.).
 */
export const filterOperatorSchema = z.enum([
  'eq',
  'neq',
  'contains',
  'not_contains',
  'starts_with',
  'ends_with',
  'gt',
  'gte',
  'lt',
  'lte',
  'in',
  'is_null',
  'is_not_null',
]);

export type FilterOperator = z.infer<typeof filterOperatorSchema>;

/**
 * Pojedynczy filtr — para (pole, operator, wartość).
 * Wartość jest unknown bo zależy od operatora (string, number, lista...).
 */
export const filterConfigSchema = z.object({
  /** Slug pola po którym filtrujemy. */
  field: z.string().min(1),
  /** Operator porównania. */
  operator: filterOperatorSchema,
  /** Wartość do porównania — typ zależy od operatora. */
  value: z.unknown(),
});

export type FilterConfig = z.infer<typeof filterConfigSchema>;

// ── Szybkie filtry (preset filtrów nad listą — chipy / dropdown) ─────────────

/**
 * Szybki filtr — nazwany preset warunków filtrowania pokazywany nad listą
 * (chip lub dropdown). Wybór presetu aplikuje wszystkie jego warunki naraz.
 * Wzorzec z SellCall (`QuickFilterConfig`). Np. „Aktywne" = `status eq active`,
 * „Z Symfonii" = `source eq symfonia`.
 */
export const quickFilterSchema = z.object({
  /** UUID presetu — generowany po stronie klienta. */
  id: z.string().guid(),
  /** Etykieta chipa/pozycji dropdowna (np. „Aktywne"). */
  label: z.string().min(1).max(100),
  /** Warunki aplikowane przy wyborze presetu (AND). */
  filters: z.array(filterConfigSchema),
  /** Czy preset jest domyślnie aktywny przy wejściu na listę. */
  isDefault: z.boolean().optional(),
  /** Kolejność wyświetlania (0-indexed). */
  order: z.number().int().nonnegative(),
});

export type QuickFilter = z.infer<typeof quickFilterSchema>;

// ── Wygląd (style wizualne edytowane w zakładce „Strona") ─────────────────────
//
// Customizacja wyglądu paska wyszukiwania+filtrów oraz kart widgetów — inspiracja
// SellCall (panele „Styl pól wyszukiwania i filtrów" + „Styl widgetu"). Wszystkie
// pola opcjonalne: brak = wygląd domyślny z motywu. Kolory jako hex `#RRGGBB`.

/** Hex koloru `#RRGGBB` — wspólny dla stylów wyglądu. */
const appearanceHexColor = z.string().regex(/^#[0-9A-Fa-f]{6}$/);

/**
 * Skala zaokrąglenia rogów (Tailwind-like) wspólna dla paska filtrów i widgetów.
 * `none`/`sm`/`md`/`lg`/`xl`/`2xl` — odwzorowanie skali z SellCall.
 */
export const roundingScaleSchema = z.enum(['none', 'sm', 'md', 'lg', 'xl', '2xl']);

export type RoundingScale = z.infer<typeof roundingScaleSchema>;

/** Skala cienia karty widgetu. */
export const shadowScaleSchema = z.enum(['none', 'sm', 'md', 'lg', 'xl']);

export type ShadowScale = z.infer<typeof shadowScaleSchema>;

/**
 * Styl bloku wyszukiwania + filtrów nad listą (zakładka „Strona").
 * `blockStyle` = preset całego paska (domyślny / karta / bez ramki), reszta pól
 * stylizuje pojedyncze pola (input wyszukiwania + dropdowny filtrów).
 */
export const filterBarStyleSchema = z.object({
  /** Preset wyglądu całego bloku. */
  blockStyle: z.enum(['default', 'card', 'plain']).optional(),
  /** Kolor tła pola (input/dropdown). */
  fieldBackgroundColor: appearanceHexColor.optional(),
  /** Kolor tekstu pola. */
  fieldTextColor: appearanceHexColor.optional(),
  /** Kolor ramki pola. */
  fieldBorderColor: appearanceHexColor.optional(),
  /** Grubość ramki pola w px (0–8). */
  fieldBorderWidth: z.number().int().min(0).max(8).optional(),
  /** Zaokrąglenie rogów pola. */
  fieldRounding: roundingScaleSchema.optional(),
});

export type FilterBarStyle = z.infer<typeof filterBarStyleSchema>;

/**
 * Styl kart widgetów nad listą (zakładka „Strona"). Odwzorowanie panelu „Styl
 * widgetu" z SellCall: tło (kolor lub gradient), efekt szkła, kolory tekstu/
 * nagłówka, zaokrąglenie, cień. Gradient (gdy ustawiony) ma pierwszeństwo nad
 * `backgroundColor`.
 */
export const widgetStyleSchema = z.object({
  /** Kolor tła karty. */
  backgroundColor: appearanceHexColor.optional(),
  /** Gradient tła (od→do) — nadpisuje `backgroundColor`. null = brak gradientu. */
  backgroundGradient: z
    .object({ from: appearanceHexColor, to: appearanceHexColor })
    .nullable()
    .optional(),
  /** Efekt szkła: półprzezroczyste tło z rozmyciem (backdrop blur). */
  glassEffect: z.boolean().optional(),
  /** Kolor tekstu (wartości) karty. */
  textColor: appearanceHexColor.optional(),
  /** Kolor nagłówka/etykiety karty. */
  headerColor: appearanceHexColor.optional(),
  /** Zaokrąglenie rogów karty. */
  rounding: roundingScaleSchema.optional(),
  /** Cień karty. */
  shadow: shadowScaleSchema.optional(),
});

export type WidgetStyle = z.infer<typeof widgetStyleSchema>;

// ── Ustawienia strony listy (zakładka „Strona" w Kreatorze Listy) ────────────

/**
 * Ustawienia całej strony listy (nie pojedynczej kolumny): ile rekordów na
 * stronę oraz które elementy paska nad listą pokazywać. Edytowane w zakładce
 * „Strona" Kreatora Listy. v1 = tylko persystencja (runtime — faktyczne użycie
 * na liście/ConfigurableTable — to follow-up po merge).
 */
export const pageConfigSchema = z.object({
  /** Własny tytuł strony nadpisujący nazwę modułu (undefined = nazwa modułu). */
  title: z.string().max(200).optional(),
  /** Własny opis/podtytuł pod tytułem (undefined = brak). */
  description: z.string().max(500).optional(),
  /** Liczba rekordów na stronę (paginacja). */
  pageSize: z.number().int().positive().max(500),
  /** Czy pokazywać pasek statystyk nad listą. */
  showStats: z.boolean(),
  /** Czy pokazywać pole wyszukiwarki nad listą. */
  showSearch: z.boolean(),
  /** Czy pokazywać pasek filtrów (per kolumna) nad listą. */
  showFilterBar: z.boolean(),
  /** Wygląd paska wyszukiwania + filtrów (opcjonalny — brak = motyw). */
  filterBarStyle: filterBarStyleSchema.optional(),
  /** Wygląd kart widgetów (opcjonalny — brak = motyw). */
  widgetStyle: widgetStyleSchema.optional(),
});

export type PageConfig = z.infer<typeof pageConfigSchema>;

/**
 * Domyślne ustawienia strony — używane gdy layout nie ma jeszcze `pageConfig`
 * (wsteczna zgodność) albo przy tworzeniu nowego widoku.
 */
export const defaultPageConfig: PageConfig = {
  pageSize: 25,
  showStats: true,
  showSearch: true,
  showFilterBar: true,
};

// ── Etykiety przycisków akcji (zakładka „Przyciski" w Kreatorze Listy) ────────

/**
 * Nadpisanie etykiety pojedynczego przycisku akcji nad listą (np. domyślne
 * „Dodaj kontrahenta" → własna nazwa). `actionId` to stały identyfikator akcji
 * (np. `create`, `export`), `customLabel` = własna etykieta (pusta/undefined =
 * użyj domyślnej). v1 = tylko persystencja edytora.
 */
export const buttonConfigSchema = z.object({
  /** Identyfikator akcji (stały, np. `create`, `refresh`, `export`). */
  actionId: z.string().min(1),
  /** Własna etykieta nadpisująca domyślną (undefined = domyślna). */
  customLabel: z.string().max(100).optional(),
});

export type ButtonConfig = z.infer<typeof buttonConfigSchema>;

// ── Własne przyciski nawigacyjne (zakładka „Przyciski" — builder) ─────────────

/**
 * Akcja własnego przycisku. Na tym etapie tylko akcje wykonalne na froncie
 * (bez backendu): przejście w aplikacji, otwarcie URL, odświeżenie listy.
 *
 * Przyciski-akcji (wyślij mail / webhook / utwórz zadanie / zmień pole itp.) =
 * de facto automatyzacja — dojdą w późniejszym etapie razem z silnikiem akcji
 * (moduł Procesy/akceptacje). Wtedy ta unia zostanie rozszerzona o kolejne
 * warianty + backendowy dispatcher.
 */
export const customButtonActionSchema = z.discriminatedUnion('type', [
  /** Przejście wewnątrz aplikacji (React Router) pod podaną ścieżkę. */
  z.object({ type: z.literal('navigate'), to: z.string().min(1) }),
  /** Otwarcie zewnętrznego URL (opcjonalnie w nowej karcie). */
  z.object({ type: z.literal('open_url'), url: z.string().min(1), newTab: z.boolean().optional() }),
  /** Odświeżenie danych listy (invalidacja zapytania). */
  z.object({ type: z.literal('refresh') }),
]);

export type CustomButtonAction = z.infer<typeof customButtonActionSchema>;

/**
 * Własny przycisk dodany przez usera w builderze (zakładka „Przyciski"). Kształt
 * portowany z SellCall (`CustomActionButton`) — wariant koloru, pozycja (pasek
 * narzędzi / akcja wiersza / akcja masowa) i akcja. Na tym etapie akcja
 * ograniczona do nawigacyjnych (patrz `customButtonActionSchema`).
 */
export const customActionButtonSchema = z.object({
  /** UUID przycisku — generowany po stronie klienta. */
  id: z.string().min(1),
  /** Etykieta na przycisku. */
  label: z.string().min(1).max(100),
  /** Nazwa ikony (lucide) — opcjonalna. */
  icon: z.string().optional(),
  /** Wariant koloru przycisku. */
  variant: z.enum(['primary', 'secondary', 'outline', 'ghost', 'danger']),
  /** Gdzie przycisk się renderuje. */
  position: z.enum(['toolbar', 'row_actions', 'bulk_actions']),
  /** Akcja odpalana po kliknięciu. */
  action: customButtonActionSchema,
  /** Kolejność wyświetlania (0-indexed). */
  order: z.number().int().nonnegative(),
});

export type CustomActionButton = z.infer<typeof customActionButtonSchema>;

// ── Formatowanie warunkowe (Krok 7 — parytet Excel) ──────────────────────────
//
// Reguły formatowania komórek/wierszy w zależności od wartości. 7 typów reguł
// inspirowanych Excel Conditional Formatting. Reguła przypisana do kolumny,
// może formatować pojedynczą komórkę (`applyTo='cell'`) albo cały wiersz
// (`applyTo='row'`). Wiele reguł na kolumnie — `priority` decyduje o kolejności
// aplikacji (niższa liczba = wyższy priorytet, last-wins per styl).
//
// Persystencja: `ListConfig.formattingRules` (jedno pole obok kolumn/sortu/
// filtrów). Brak osobnej tabeli — decyzja Eugenii 2026-05-29.

/**
 * Styl pojedynczej komórki/wiersza nakładany przez regułę.
 * Wszystkie pola opcjonalne — reguła nakłada tylko to co zdefiniowane,
 * resztę zostawia z domyślnego renderu kolumny.
 */
export const cellStyleSchema = z.object({
  /** Kolor tła (hex `#RRGGBB`). */
  backgroundColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .optional(),
  /** Kolor czcionki (hex `#RRGGBB`). */
  color: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .optional(),
  /** Kolor obramowania (hex `#RRGGBB`). */
  borderColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .optional(),
  /** Grubość obramowania w pikselach (1–5). */
  borderWidth: z.number().int().min(1).max(5).optional(),
  /** Pogrubienie. */
  fontWeight: z.enum(['normal', 'bold']).optional(),
  /** Kursywa. */
  fontStyle: z.enum(['normal', 'italic']).optional(),
});

export type CellStyle = z.infer<typeof cellStyleSchema>;

// ── Condition shapes per typ reguły ──────────────────────────────────────────

/**
 * Typ 1 — Format only cells that contain (Highlight Cells Rules).
 * Najpopularniejszy: operator + wartość. Dla `between`/`not_between` używamy
 * też `value2`. Dla `is_null`/`is_not_null` wartość ignorowana.
 */
export const highlightCellsConditionSchema = z.object({
  operator: z.enum([
    'gt',
    'gte',
    'lt',
    'lte',
    'eq',
    'neq',
    'contains',
    'not_contains',
    'starts_with',
    'ends_with',
    'between',
    'not_between',
    'is_null',
    'is_not_null',
  ]),
  value: z.union([z.string(), z.number(), z.boolean()]).nullable(),
  value2: z.union([z.string(), z.number()]).optional(),
});

export type HighlightCellsCondition = z.infer<typeof highlightCellsConditionSchema>;

/**
 * Typ 2 — Format only top or bottom ranked values.
 * Np. „top 10% wartości w kolumnie" albo „top 3 wiersze".
 */
export const topBottomConditionSchema = z.object({
  mode: z.enum(['top', 'bottom']),
  unit: z.enum(['count', 'percent']),
  n: z.number().int().positive(),
});

export type TopBottomCondition = z.infer<typeof topBottomConditionSchema>;

/**
 * Typ 3 — Format only values that are above or below average.
 * Średnia liczona dynamicznie z aktualnego zbioru wierszy. `or_equal`
 * dorzuca przypadek równości (Excel-style).
 */
export const aboveBelowAvgConditionSchema = z.object({
  mode: z.enum(['above', 'below', 'above_or_equal', 'below_or_equal']),
});

export type AboveBelowAvgCondition = z.infer<typeof aboveBelowAvgConditionSchema>;

/**
 * Typ 4 — Format only unique or duplicate values.
 * `unique`: wartość pojawia się dokładnie raz w kolumnie.
 * `duplicate`: wartość pojawia się więcej niż raz.
 */
export const uniqueDuplicateConditionSchema = z.object({
  mode: z.enum(['unique', 'duplicate']),
});

export type UniqueDuplicateCondition = z.infer<typeof uniqueDuplicateConditionSchema>;

/**
 * Typ 5 — Format only changing values.
 * Wartość różna od wartości w poprzednim wierszu (po sortowaniu po
 * `sortColumn`). Używane do oznaczania granic grup.
 */
export const changingValuesConditionSchema = z.object({
  /** Identyfikator kolumny po której sortujemy zbiór wierszy przed wykryciem zmian. */
  sortColumn: z.string().min(1),
});

export type ChangingValuesCondition = z.infer<typeof changingValuesConditionSchema>;

/**
 * Typ 6 — Format all cells based on their values (gradient / data bar / icon set).
 *
 * Excel grupuje pod jednym typem cztery różne style prezentacji:
 *  - `'2color'` — gradient od koloru min do koloru max.
 *  - `'3color'` — gradient min → mid → max (z opcjonalnym midpoint % 0–100).
 *  - `'data_bar'` — pasek tła o długości proporcjonalnej do wartości.
 *  - `'icon_set'` — ikona obok wartości z palety (strzałki 3/5, traffic 3,
 *    gwiazdki 5).
 *
 * Min/max liczone dynamicznie z aktualnego zbioru wierszy.
 */
export const allCellsConditionSchema = z.discriminatedUnion('subType', [
  z.object({
    subType: z.literal('2color'),
    colorMin: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
    colorMax: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
  }),
  z.object({
    subType: z.literal('3color'),
    colorMin: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
    colorMid: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
    colorMax: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
    /** Próg środkowy 0–100% (default 50). */
    midpoint: z.number().min(0).max(100).optional(),
  }),
  z.object({
    subType: z.literal('data_bar'),
    color: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
    /** Czy pokazywać wartość liczbową obok paska. */
    showValue: z.boolean().optional(),
  }),
  z.object({
    subType: z.literal('icon_set'),
    setName: z.enum(['arrows_3', 'arrows_5', 'traffic_3', 'stars_5']),
    /** Czy odwrócić mapowanie (max → ikona „zła" zamiast „dobrej"). */
    reverse: z.boolean().optional(),
  }),
]);

export type AllCellsCondition = z.infer<typeof allCellsConditionSchema>;

/**
 * Typ 7 — Use a formula to determine which cells to format.
 *
 * Wyrażenie używające składni `@columnId operator wartość`. Operatory: `>`,
 * `<`, `>=`, `<=`, `=`, `!=`, kombinatory `AND`, `OR`, nawiasy `()`. Stringi
 * w cudzysłowiu pojedynczym `'...'`. Brak `eval` po stronie frontu — własny
 * parser AST z white-listą operatorów.
 *
 * Przykład: `@status = 'blocked' AND @amount > 1000`.
 */
export const formulaConditionSchema = z.object({
  expression: z.string().min(1).max(500),
});

export type FormulaCondition = z.infer<typeof formulaConditionSchema>;

// ── Pełna reguła (discriminated union po `type`) ─────────────────────────────

/**
 * Wspólne pola każdej reguły niezależnie od typu.
 */
const formattingRuleBase = z.object({
  /** UUID reguły — generowany po stronie klienta przy tworzeniu. */
  id: z.string().guid(),
  /** Etykieta dla usera w managerze reguł (np. „Faktura overdue"). */
  name: z.string().min(1).max(100).optional(),
  /** Kolumna do której reguła jest przypisana (z menu kontekstowego nagłówka). */
  columnId: z.string().min(1),
  /** Czy reguła formatuje samą komórkę kolumny czy cały wiersz. */
  applyTo: z.enum(['cell', 'row']),
  /** Priorytet 0..N — niższa liczba = wyższy priorytet (aplikuje się pierwsza). */
  priority: z.number().int().nonnegative(),
  /** Toggle on/off bez kasowania reguły. */
  enabled: z.boolean().default(true),
});

/**
 * Pełna reguła formatowania — dyskryminowana unia po `type`. Każdy wariant
 * ma własny shape pola `condition`. Reguły z typami `'all_cells'` nie mają
 * pola `style` (gradient/data_bar/icon_set sam definiuje wygląd komórki);
 * pozostałe typy mają `style: CellStyle`.
 */
export const formattingRuleSchema = z.discriminatedUnion('type', [
  formattingRuleBase.extend({
    type: z.literal('highlight_cells'),
    condition: highlightCellsConditionSchema,
    style: cellStyleSchema,
  }),
  formattingRuleBase.extend({
    type: z.literal('top_bottom'),
    condition: topBottomConditionSchema,
    style: cellStyleSchema,
  }),
  formattingRuleBase.extend({
    type: z.literal('above_below_avg'),
    condition: aboveBelowAvgConditionSchema,
    style: cellStyleSchema,
  }),
  formattingRuleBase.extend({
    type: z.literal('unique_duplicate'),
    condition: uniqueDuplicateConditionSchema,
    style: cellStyleSchema,
  }),
  formattingRuleBase.extend({
    type: z.literal('changing_values'),
    condition: changingValuesConditionSchema,
    style: cellStyleSchema,
  }),
  formattingRuleBase.extend({
    type: z.literal('all_cells'),
    condition: allCellsConditionSchema,
    // Bez `style` — gradient/data_bar/icon_set definiuje wygląd.
  }),
  formattingRuleBase.extend({
    type: z.literal('formula'),
    condition: formulaConditionSchema,
    style: cellStyleSchema,
  }),
]);

export type FormattingRule = z.infer<typeof formattingRuleSchema>;
export type FormattingRuleType = FormattingRule['type'];

// ── Tryb widoku (lista / karty / Kanban) ─────────────────────────────────────

/**
 * Tryb prezentacji listy: klasyczna tabela, kafelki (karty), tablica Kanban
 * (grupowanie po kolumnie typu select) lub kalendarz miesięczny (pozycjonowanie
 * po kolumnie daty — `calendarConfig`). Przełącznik renderowany w pasku tabeli;
 * wybrany tryb zapisuje się jako `defaultViewMode` i wczytuje przy następnym
 * wejściu na listę.
 */
export const viewModeSchema = z.enum(['list', 'cards', 'kanban', 'calendar']);

export type ViewMode = z.infer<typeof viewModeSchema>;

// ── Kanban (grupowanie + wygląd kafelka + styl tablicy) ──────────────────────
//
// Konfiguracja tablicy Kanban dla widoku listy — wspólna dla modułów systemowych
// i własnych (oba zapisują w `list_config.kanbanConfig`). Odwzorowanie zakładki
// „Kanban" z SellCalla: pole grupujące + pola na kartce + opcjonalny bogaty układ
// kafelka z designera + styl tablicy.

/** Zaokrąglenie rogów kolumny tablicy. */
export const kanbanColumnRadiusSchema = z.enum(['none', 'small', 'medium', 'large']);

export type KanbanColumnRadius = z.infer<typeof kanbanColumnRadiusSchema>;

/**
 * Styl nagłówka kolumny Kanban (czcionka, wyrównanie, wskaźnik koloru, licznik).
 */
export const kanbanHeaderStyleSchema = z.object({
  /** Rozmiar czcionki nagłówka w px. */
  fontSize: z.number().int().min(8).max(48),
  /** Grubość czcionki nagłówka. */
  fontWeight: z.enum(['normal', 'medium', 'semibold', 'bold']),
  /** Padding nagłówka w px. */
  padding: z.number().int().min(0).max(64),
  /** Wyrównanie tekstu nagłówka. */
  align: z.enum(['left', 'center', 'right']),
  /** Wskaźnik koloru opcji: kropka, pasek lub brak. */
  colorIndicator: z.enum(['dot', 'bar', 'none']),
  /** Styl licznika rekordów: chip (badge) lub zwykły tekst. */
  counterStyle: z.enum(['badge', 'plain']),
});

export type KanbanHeaderStyle = z.infer<typeof kanbanHeaderStyleSchema>;

/**
 * Styl całej tablicy Kanban — szerokości/odstępy/padding/tło/obramowanie kolumn
 * + styl nagłówka. Odwzorowanie panelu „Styl tablicy" z SellCalla.
 */
export const kanbanBoardStyleSchema = z.object({
  /** Szerokość kolumny w px. */
  columnWidth: z.number().int().min(180).max(640),
  /** Zaokrąglenie rogów kolumny. */
  columnRadius: kanbanColumnRadiusSchema,
  /** Odstęp między kafelkami w px. */
  cardGap: z.number().int().min(0).max(48),
  /** Padding wewnętrzny kafelka w px. */
  cardPadding: z.number().int().min(0).max(48),
  /** Kolor tła kolumny (hex `#RRGGBB`) lub null = domyślny. */
  columnBgColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .nullable(),
  /** Czy rysować obramowanie kolumny. */
  showColumnBorder: z.boolean(),
  /** Styl nagłówka kolumny. */
  header: kanbanHeaderStyleSchema,
});

export type KanbanBoardStyle = z.infer<typeof kanbanBoardStyleSchema>;

/**
 * Domyślny styl tablicy — używany gdy `kanbanConfig.boardStyle` nie ustawiony
 * (wsteczna zgodność + nowo utworzona konfiguracja Kanban).
 */
export const defaultKanbanBoardStyle: KanbanBoardStyle = {
  columnWidth: 288,
  columnRadius: 'large',
  cardGap: 8,
  cardPadding: 10,
  columnBgColor: null,
  showColumnBorder: true,
  header: {
    fontSize: 14,
    fontWeight: 'semibold',
    padding: 10,
    align: 'left',
    colorIndicator: 'dot',
    counterStyle: 'badge',
  },
};

/**
 * Konfiguracja Kanban dla widoku listy.
 *
 * `cardLayout` to opcjonalny bogaty układ kafelka z wizualnego designera
 * („Edytuj wygląd karty") — gdy ustawiony, ma pierwszeństwo nad `cardFields`.
 * Trzymany jako drzewo `LayoutElement[]` (kształt 1:1 z layoutem formularza —
 * nested `children`). Walidacja strukturalna/semantyczna tego drzewa odbywa się
 * po stronie backendu przez istniejący walidator layoutu (max głębokość,
 * unikalność id) — tu trzymamy go przepustowo, żeby nie duplikować rekurencyjnego
 * schematu w paczce shared.
 */
export const kanbanConfigSchema = z.object({
  /** Identyfikator kolumny/pola grupującego (typu select — wbudowane lub ext-field). */
  groupByFieldId: z.string().min(1),
  /** Identyfikatory pól pokazywanych na kafelku (prosty układ, gdy brak `cardLayout`). */
  cardFields: z.array(z.string().min(1)).default([]),
  /** Bogaty układ kafelka z designera (override `cardFields`) lub null = prosty układ. */
  cardLayout: z.array(z.record(z.string(), z.unknown())).nullable().default(null),
  /** Styl tablicy (opcjonalny — brak = `defaultKanbanBoardStyle`). */
  boardStyle: kanbanBoardStyleSchema.optional(),
});

export type KanbanConfig = z.infer<typeof kanbanConfigSchema>;

/**
 * Konfiguracja widoku kalendarza rekordów modułu.
 *
 * Odwołania do pól to SLUG-i (parytet z `kanbanConfig.groupByFieldId`, które mimo
 * nazwy też jest slugiem). `startFieldSlug` pozycjonuje wpis; opcjonalny
 * `endFieldSlug` sprawia, że wpis pokrywa cały zakres dni [start, end]; opcjonalny
 * `statusFieldSlug` (pole select) koloruje wpisy kolorem z `options[].color`
 * + rysuje legendę. Wszystko poza `startFieldSlug` opcjonalne — schemat rozszerzalny.
 */
export const calendarConfigSchema = z.object({
  /** Slug pola daty startu (date/datetime) — po nim pozycjonowany jest wpis. */
  startFieldSlug: z.string().min(1),
  /** Slug pola daty końca — gdy ustawione, wpis pokrywa cały zakres [start, end]. */
  endFieldSlug: z.string().min(1).optional(),
  /** Slug pola statusu (select) — kolor wpisu z `options[].color` + legenda. */
  statusFieldSlug: z.string().min(1).optional(),
  /**
   * Slug pola etykiety wpisu (co pokazać na pasku wydarzenia). Gdy brak — silnik
   * list bierze kolumnę tytułową listy, a widok modułu custom `record_name`
   * (fallback: skrócone id rekordu).
   */
  labelFieldSlug: z.string().min(1).optional(),
});

export type CalendarConfig = z.infer<typeof calendarConfigSchema>;

// ── Domyślny tryb STRONY REKORDÓW MODUŁU (Kreator, moduły custom) ───────────
//
// Osobne pole/schemat od `defaultViewMode` (wyżej) — TAMTO steruje WEWNĘTRZNYM
// renderem `ConfigurableTable` (lista/karty/Kanban w pasku samej tabeli).
// `ModuleRecordsPage` (strona rekordów modułu custom) ma WŁASNY, zewnętrzny
// przełącznik widoku NAD całą stroną (tabela/galeria/kalendarz/Kanban —
// `CalendarView`/`GalleryView`/`KanbanBoard` renderowane przez stronę, nie
// przez `ConfigurableTable`), persystowany per-user (`useListingPreferences`),
// poza `list_config`. `moduleRecordsViewModeSchema` opisuje WŁAŚNIE ten,
// stronowy zestaw trybów — osobny od `viewModeSchema` mimo częściowo wspólnego
// słownictwa (oba mają dziś `'calendar'`/`'kanban'`), bo żyje w innym miejscu
// (per-user `useListingPreferences`, nie `list_config`) i steruje innym, tj.
// zewnętrznym przełącznikiem strony, nie wewnętrznym renderem `ConfigurableTable`.
export const moduleRecordsViewModeSchema = z.enum(['table', 'gallery', 'calendar', 'kanban']);

export type ModuleRecordsViewMode = z.infer<typeof moduleRecordsViewModeSchema>;

// ── Pełna konfiguracja widoku ────────────────────────────────────────────────

/**
 * Komplet konfiguracji jednego widoku listy.
 *
 * Trzymany w bazie jako JSON w kolumnie `list_config`.
 * Każdy layout listy ma dokładnie jeden taki obiekt.
 *
 * Pola które dojdą w kolejnych krokach roadmapy (szybkie filtry, ustawienia
 * strony, etykiety przycisków) zostaną dorzucone tu — dlatego schema
 * zostawia możliwość rozszerzania bez breaking change.
 */
export const listConfigSchema = z.object({
  /** Lista kolumn z ustawieniami widoczności/kolejności/szerokości. */
  columns: z.array(columnConfigSchema),
  /** Domyślne sortowanie (lub null = bez sortowania). */
  defaultSort: sortConfigSchema.nullable(),
  /** Lista filtrów stosowanych domyślnie przy wejściu na listę. */
  defaultFilters: z.array(filterConfigSchema),
  /**
   * Reguły formatowania warunkowego — Krok 7 (parytet Excel). Opcjonalne dla
   * wstecznej zgodności (istniejące widoki bez tego pola dalej działają).
   */
  formattingRules: z.array(formattingRuleSchema).optional().default([]),
  /**
   * Szybkie filtry (presety nad listą) — Krok 3. Opcjonalne (wsteczna zgodność).
   */
  quickFilters: z.array(quickFilterSchema).optional().default([]),
  /**
   * Ustawienia strony listy (pageSize + przełączniki statystyk/wyszukiwarki/
   * paska filtrów) — zakładka „Strona". Opcjonalne (wsteczna zgodność —
   * starsze widoki bez tego pola dalej działają, runtime używa defaultów).
   */
  pageConfig: pageConfigSchema.optional(),
  /**
   * Własne etykiety przycisków akcji nad listą — zakładka „Przyciski".
   * Opcjonalne (wsteczna zgodność).
   */
  buttonsConfig: z.array(buttonConfigSchema).optional(),
  /**
   * Własne przyciski nawigacyjne dodane w builderze — zakładka „Przyciski".
   * Opcjonalne (wsteczna zgodność). Akcje na tym etapie tylko nawigacyjne.
   */
  customActionButtons: z.array(customActionButtonSchema).optional(),
  /**
   * Akcje list włączone w tym widoku — zakładka „Akcje" (PHASE 25). Referencje do
   * katalogu w kodzie (`list-actions.ts`) przez `actionId` + kolejność; metadane
   * (etykieta, ikona, bramki modułu/uprawnień) czytane z katalogu, nie duplikowane.
   * Opcjonalne (wsteczna zgodność — brak = tylko akcje domyślne z seeda).
   */
  listActions: z.array(listActionConfigSchema).optional(),
  /**
   * Domyślny tryb prezentacji listy (lista / karty / Kanban) wczytywany przy
   * wejściu na listę. Opcjonalne (wsteczna zgodność) — brak = `'list'`.
   */
  defaultViewMode: viewModeSchema.optional().default('list'),
  /**
   * Widgety pulpitu KPI nad listą — zakładka „Widgety" w Kreatorze Listy.
   * Opcjonalne (wsteczna zgodność — starsze widoki bez tego pola działają,
   * runtime renderuje pasek tylko gdy lista niepusta). Pełny shape w
   * `list-view-widgets.ts`.
   */
  widgets: widgetsConfigSchema.optional(),
  /**
   * Konfiguracja tablicy Kanban (pole grupujące + pola na kartce + wygląd kafelka
   * + styl tablicy) — zakładka „Kanban" w Kreatorze Listy. Opcjonalne (wsteczna
   * zgodność — listy bez Kanbana nie mają tego pola; runtime pokazuje empty-state
   * gdy brak konfiguracji a user przełączy na widok Kanban).
   */
  kanbanConfig: kanbanConfigSchema.optional(),
  /**
   * Konfiguracja widoku kalendarza (pole startu + opcjonalny koniec dla zakresu
   * + opcjonalny status do kolorowania). Opcjonalne (wsteczna zgodność — moduły bez
   * skonfigurowanego kalendarza nie mają tego pola; runtime auto-wykrywa pole daty).
   */
  calendarConfig: calendarConfigSchema.optional(),
  /**
   * Domyślny tryb STRONY REKORDÓW MODUŁU (tabela/galeria/kalendarz/Kanban) —
   * czytany WYŁĄCZNIE przez `ModuleRecordsPage` jako fallback, gdy user NIE MA
   * jeszcze własnej (per-user) preferencji widoku strony. Per-user preferencja
   * (`useListingPreferences`), gdy istnieje, zawsze wygrywa nad tym polem. Nie
   * mylić z `defaultViewMode` (inny mechanizm — patrz komentarz przy
   * `moduleRecordsViewModeSchema`). Opcjonalne (wsteczna zgodność — brak =
   * strona startuje w widoku tabeli, jak dotychczas).
   */
  defaultModuleRecordsViewMode: moduleRecordsViewModeSchema.optional(),
});

export type ListConfig = z.infer<typeof listConfigSchema>;

/**
 * Pusta konfiguracja — używana jako default przy tworzeniu nowego layoutu
 * jeśli klient nie poda swojej.
 */
export const emptyListConfig: ListConfig = {
  columns: [],
  defaultSort: null,
  defaultFilters: [],
  formattingRules: [],
  quickFilters: [],
  pageConfig: defaultPageConfig,
  buttonsConfig: [],
  customActionButtons: [],
  defaultViewMode: 'list',
  widgets: [],
};
